import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Trophy, Shield, GripVertical, RefreshCw, Inbox, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

/**
 * AdminTeamAssignments — HTML5 native drag-and-drop board that lets an admin
 * assign every ABFA team to its designated competition. Zero library
 * dependencies. Optimistic UI with server sync + rollback on error.
 */
const AdminTeamAssignments = () => {
  const [data, setData] = useState({ competitions: [], groups: {}, unassigned: [] });
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null); // competition_id currently being hovered

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/team-assignments`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Drag payload = { team_id, from_competition_id | null (unassigned) }
  const onDragStart = (e, team_id, from_competition_id) => {
    e.dataTransfer.setData("application/json", JSON.stringify({ team_id, from_competition_id }));
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e, competition_id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== competition_id) setDragOver(competition_id);
  };

  const onDragLeave = () => setDragOver(null);

  const onDrop = async (e, to_competition_id) => {
    e.preventDefault();
    setDragOver(null);
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData("application/json"));
    } catch { return; }
    const { team_id, from_competition_id } = payload;
    if (from_competition_id === to_competition_id) return; // no-op

    // Optimistic UI
    const snapshot = data;
    setData((prev) => {
      const next = {
        ...prev,
        groups: { ...prev.groups },
        unassigned: [...prev.unassigned],
      };
      let team = null;
      if (from_competition_id) {
        next.groups[from_competition_id] = next.groups[from_competition_id].filter((t) => {
          if (t.team_id === team_id) { team = t; return false; }
          return true;
        });
      } else {
        next.unassigned = next.unassigned.filter((t) => {
          if (t.team_id === team_id) { team = t; return false; }
          return true;
        });
      }
      if (!team) return prev;
      if (to_competition_id) {
        next.groups[to_competition_id] = [...(next.groups[to_competition_id] || []), team];
      } else {
        next.unassigned = [...next.unassigned, team];
      }
      return next;
    });

    try {
      const params = new URLSearchParams();
      if (from_competition_id) params.set("from_competition_id", from_competition_id);
      if (to_competition_id) params.set("to_competition_id", to_competition_id);
      await axios.put(`${API}/admin/teams/${team_id}/move?${params.toString()}`, null, { withCredentials: true });
      toast.success(`Moved to ${to_competition_id ? competitionName(to_competition_id) : "Unassigned"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Move failed — reverted");
      setData(snapshot);
    }
  };

  const competitionName = (id) =>
    data.competitions.find((c) => c.competition_id === id)?.name || "";

  const removeFromCompetition = async (team_id, competition_id) => {
    const snapshot = data;
    setData((prev) => ({
      ...prev,
      groups: { ...prev.groups, [competition_id]: prev.groups[competition_id].filter((t) => t.team_id !== team_id) },
      unassigned: [...prev.unassigned, prev.groups[competition_id].find((t) => t.team_id === team_id)].filter(Boolean),
    }));
    try {
      await axios.delete(`${API}/admin/competitions/${competition_id}/teams/${team_id}`, { withCredentials: true });
      toast.success("Removed from competition");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
      setData(snapshot);
    }
  };

  const TeamCard = ({ team, from_competition_id }) => (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, team.team_id, from_competition_id)}
      className="group bg-white border border-gray-200 hover:border-[#FFC72C] p-2 flex items-center gap-2 cursor-grab active:cursor-grabbing transition-colors"
      data-testid={`team-card-${team.team_id}`}
    >
      <GripVertical className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 shrink-0" />
      <img
        src={team.logo_url || "https://placehold.co/32x32/09090b/FFF?text=T"}
        alt=""
        className="w-8 h-8 object-contain shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-heading font-bold uppercase truncate">{team.name}</p>
        <p className="text-[10px] text-gray-500 font-mono">{team.short_name}</p>
      </div>
      {from_competition_id && (
        <button
          onClick={() => removeFromCompetition(team.team_id, from_competition_id)}
          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
          title="Remove from this competition"
          data-testid={`btn-remove-${team.team_id}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  const Column = ({ title, subtitle, icon: Icon, competitionId, teams, colorClass = "bg-[#009E60]" }) => {
    const isHover = dragOver === competitionId;
    return (
      <div
        onDragOver={(e) => onDragOver(e, competitionId)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, competitionId)}
        className={`flex flex-col bg-gray-50 border-2 transition-all min-h-[400px] ${isHover ? "border-[#FFC72C] bg-yellow-50 scale-[1.01]" : "border-transparent"}`}
        data-testid={`column-${competitionId || "unassigned"}`}
      >
        <div className={`${colorClass} text-white px-4 py-3 flex items-center gap-2`}>
          <Icon className="w-4 h-4" />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold uppercase text-sm truncate">{title}</p>
            {subtitle && <p className="text-[10px] uppercase tracking-widest opacity-80 truncate">{subtitle}</p>}
          </div>
          <Badge className="bg-white/20 text-white hover:bg-white/20 text-xs" data-testid={`count-${competitionId || "unassigned"}`}>
            {teams.length}
          </Badge>
        </div>
        <div className="p-3 space-y-2 flex-1">
          {teams.length === 0 && (
            <p className="text-xs text-gray-400 italic text-center py-8 border-2 border-dashed border-gray-200">
              Drop teams here
            </p>
          )}
          {teams.map((t) => <TeamCard key={t.team_id} team={t} from_competition_id={competitionId} />)}
        </div>
      </div>
    );
  };

  return (
    <AdminLayout title="Team Assignments">
      <div className="max-w-[1600px] mx-auto space-y-4" data-testid="admin-team-assignments">
        <div className="bg-white p-6 border border-gray-200 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-2xl font-bold uppercase text-[#09090b] mb-1">Team Assignments</h2>
            <p className="text-sm text-gray-500 max-w-2xl">
              Drag any team into its designated competition. A team can appear in more than one competition
              (e.g. Premier Division + FA Cup). Drop into <strong>Unassigned</strong> to clear all
              competitions for that team.
            </p>
          </div>
          <button
            onClick={load}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Refresh"
            data-testid="btn-refresh-assignments"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1,2,3,4].map((i) => <Skeleton key={i} className="h-96" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Column
              title="Unassigned"
              subtitle="Teams without a competition"
              icon={Inbox}
              competitionId={null}
              teams={data.unassigned}
              colorClass="bg-gray-800"
            />
            {data.competitions.map((c) => (
              <Column
                key={c.competition_id}
                title={c.name}
                subtitle={c.season || c.type}
                icon={c.type === "cup" ? Trophy : Shield}
                competitionId={c.competition_id}
                teams={data.groups[c.competition_id] || []}
                colorClass={
                  c.name.toLowerCase().includes("premier") ? "bg-[#009E60]" :
                  c.name.toLowerCase().includes("first") ? "bg-[#FFC72C] text-black" :
                  c.name.toLowerCase().includes("second") ? "bg-blue-600" :
                  "bg-purple-600"
                }
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTeamAssignments;
